import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { getLocalLocation } from "@/lib/location";
import { CustomerBottomNav } from "@/components/customer/CustomerBottomNav";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bus, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";

const REGION_TAG = "bus-booking";

export default function BusBookingPage() {
    const { user } = useAuth();
    const localLoc = getLocalLocation();
    const homeDistrict = user?.district_slug || localLoc?.district_slug || "ernakulam";
    const [email, setEmail] = useState(user?.email || "");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !email.includes("@")) return toast.error("Enter a valid email");
        setBusy(true);
        try {
            const r = await api.post("/waitlist", {
                country_code: "IN",
                state_slug: REGION_TAG,
                city: (localLoc?.city || homeDistrict),
                contact_email: email,
                contact_mobile: user?.mobile_number || null,
            });
            setDone(true);
            if (r.data?.already_registered) toast("You're already on the list — we'll ping you soon.");
            else toast.success("You're on the waitlist ✓");
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-surface pb-24 lg:pb-6" data-testid="bus-booking-page">
            <header className="sticky top-0 z-30 border-b border-[var(--border-soft)] bg-white">
                <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
                    <Link to={`/home?district_slug=${homeDistrict}`} className="rounded-full p-2 hover:bg-neutral-100" data-testid="bus-back">
                        <ArrowLeft size={18} />
                    </Link>
                    <div className="font-heading text-lg font-bold text-[var(--text-primary)]">Bus Booking</div>
                </div>
            </header>
            <main className="mx-auto max-w-3xl px-4 py-8">
                <div className="card-hero !p-8 sm:!p-10">
                    <div
                        className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl text-white shadow"
                        style={{ background: "linear-gradient(135deg,#0EA5E9,#0369A1)" }}
                    >
                        <Bus size={30} />
                    </div>
                    <h1 className="section-title text-center text-2xl sm:text-3xl">Coming Soon</h1>
                    <p className="mx-auto mt-2 max-w-lg text-center text-[var(--text-muted)]">
                        Kerala bus schedules and KSRTC/private-operator offers coming your way. Get notified the moment we launch.
                    </p>
                    {done ? (
                        <div className="mx-auto mt-6 flex max-w-md items-center justify-center gap-2 rounded-full bg-[color-mix(in_srgb,var(--brand-green)_15%,white)] px-4 py-2 font-heading text-sm font-semibold text-[var(--brand-green)]" data-testid="bus-waitlist-done">
                            <CheckCircle2 size={16} /> You&apos;re on the list — we&apos;ll ping you at launch.
                        </div>
                    ) : (
                        <form onSubmit={submit} className="mx-auto mt-6 flex max-w-md flex-col gap-3 sm:flex-row" data-testid="bus-waitlist-form">
                            <div className="flex-1">
                                <Label className="sr-only" htmlFor="bus-email">Email</Label>
                                <Input
                                    id="bus-email"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="bus-waitlist-email"
                                    required
                                />
                            </div>
                            <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid="bus-waitlist-submit">
                                {busy && <Loader2 size={14} className="animate-spin" />} Notify me
                            </button>
                        </form>
                    )}
                </div>

                <div className="mt-6 grid gap-3 sm:grid-cols-3">
                    {[
                        { emoji: "🚏", label: "Live seat availability" },
                        { emoji: "💳", label: "One-tap booking" },
                        { emoji: "🎟️", label: "OfferNest-exclusive discounts" },
                    ].map((f) => (
                        <div key={f.label} className="card-soft flex items-center gap-3">
                            <div className="text-2xl" aria-hidden>{f.emoji}</div>
                            <div className="font-heading text-sm font-semibold text-[var(--text-primary)]">{f.label}</div>
                        </div>
                    ))}
                </div>
            </main>
            <CustomerBottomNav homeHref={`/home?district_slug=${homeDistrict}`} />
        </div>
    );
}
